import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import { Box } from "@mui/material";
import type { DateTime } from "luxon";
import { type FC, useCallback, useDeferredValue, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../../../../contexts/analytics/hooks";
import { useHospitalData } from "../../../../contexts/HospitalDataProvider";
import { useLatestRef } from "../../../../hooks/useLatestRef";
import { NEUTRAL } from "../../../../theme/tokens";
import type { NurseInShift } from "../../../../utils/models";
import { SheetActionRow } from "../../../UIToolkit/Clickable/SheetActionRow";
import { SystemSearchField } from "../../../UIToolkit/Inputs/SystemSearchField";
import { SheetStep } from "../../../UIToolkit/Popups/SheetStep";
import { SheetStepButton } from "../../../UIToolkit/Popups/SheetStepButton";
import type { GapsScopedShiftGroup } from "../gapsGroupSheets";
import { GAPS_DAY_LABEL_FORMAT, joinLabelParts } from "../gapsLabels";
import { GAPS_SHORTLIST_SIZE } from "../gapsRowLayout";
import { useGapsRequestLabels } from "../openShifts/useGapsRequestLabels";
import { GapsAddCandidateList } from "./GapsAddCandidateList";
import { GapsAddStepHints } from "./GapsAddStepHints";
import { GapsAddWarningDialog } from "./GapsAddWarningDialog";
import { GapsDateField } from "./GapsDateField";
import { GapsJobBadge } from "./GapsJobBadge";
import { GapsAddOrder, matchesCandidateSearch } from "./gapsAddCandidates";
import type { GapsAddContext } from "./gapsAddContext";
import { type GapsAddCandidate, useGapsAddCandidates } from "./useGapsAddCandidates";
import { useGapsAddStaff } from "./useGapsAddStaff";

type Props = {
	context: GapsAddContext;
	/**
	 * The linked request is not in the live list: never found, or gone since it was (filled, deleted, merged). Unless it
	 * is this sheet's own add that closed it — only possible while that add is WRITING — the add would complete nothing,
	 * so it is refused as gone, at the tap and again at the write (`useGapsAddStaff`).
	 */
	isLinkedRequestLost: boolean;
	/** The day's groups, building-wide, to say who is already on a shift that day. */
	dayGroups: readonly GapsScopedShiftGroup[];
	date: DateTime;
	/** The footer's way out: back to the group picker, or `undefined` to close (a scoped opening). */
	onBack: (() => void) | undefined;
	onClose: () => void;
	onAdded: (created: NurseInShift) => void;
	/** "Post instead"; `undefined` on a day that is over, where the row is left out. */
	onPostInstead: ((groupKey: string) => void) | undefined;
};

const SHEET = "gaps-add-staff";

/**
 * Who to add (P3-7): the people who could take the shift, best first — by cost with wages visible, alphabetically
 * without — three at first and the rest one tap away, with search pinned beside them reaching everyone. Picking a
 * person selects them; the footer's "Add <name>" is the write, so a stray tap on a long list never adds anybody.
 *
 * Each row says what the ranking was made of — the predicted cost and the overtime it causes — and whether the person
 * already works a shift that day, since nobody is left out for that (the warnings catch a real overlap). An unlinked
 * add under a section that cannot say which job it is (`isSectionWriteBlocked`) is refused, with the reason.
 */
export const GapsAddCandidatesStep: FC<Props> = ({ context, isLinkedRequestLost, dayGroups, date, onBack, onClose, onAdded, onPostInstead }) => {
	const { t } = useTranslation();
	const { track } = useAnalytics();
	const { chosenDepartment } = useHospitalData();
	const { candidates, poolSize, order, isRanking, isRankingFailed, isRosterLoading, isRosterError } = useGapsAddCandidates(context);
	const { hoursLabel } = useGapsRequestLabels();
	const { add, cancelWarnings, warnings, isBusy, isWriting, isWriteBlocked } = useGapsAddStaff({ context, isLinkedRequestLost, date, onAdded });
	// Our own add closes a request it fills: while it WRITES, and through the exit that follows, that is not "gone". The
	// checks before it and an open warning are not writing — a request that leaves the list then is gone, and says so.
	const isLinkedRequestGone = isLinkedRequestLost && !isWriting;
	const [search, setSearch] = useState<string>("");
	const deferredSearch = useDeferredValue(search);
	const [isShowingAll, setIsShowingAll] = useState<boolean>(false);
	const [selectedId, setSelectedId] = useState<number | null>(null);

	const { group, locationName, times, linkedRequest } = context;
	const query = deferredSearch.trim();
	const matches = useMemo(
		() => (query === "" ? candidates : candidates.filter((candidate) => matchesCandidateSearch(candidate.name, query))),
		[candidates, query],
	);
	const visible = query !== "" || isShowingAll ? matches : matches.slice(0, GAPS_SHORTLIST_SIZE);
	const hiddenCount = matches.length - visible.length;
	const selected = candidates.find((candidate) => candidate.nurse.id === selectedId);

	// What is being added, from the add's own context, whichever way the sheet was opened: the job and the hours the person
	// is added for (a missing row's slot, the group's window, or a linked request's own hours) in the home's short form,
	// then the unit and the day — never a group window that could disagree with those hours. A hole in the unassigned
	// pseudo-unit carries an empty unit name, which says the same as no unit at all.
	const subtitle = joinLabelParts([
		`${group.jobLabel} ${hoursLabel(times.startTime, times.endTime)}`,
		locationName === null || locationName === "" ? t("gaps.addSheet.noUnitYet") : locationName,
		date.toFormat(GAPS_DAY_LABEL_FORMAT),
	]);

	const listLabel = query
		? t("gaps.addSheet.hits", { count: matches.length, total: poolSize, query })
		: order === GapsAddOrder.Cost
			? t("gaps.addSheet.byCost", { count: poolSize })
			: t("gaps.addSheet.byName", { count: poolSize });

	// One STABLE callback for every row, so a memoised row that did not change skips re-rendering — on every deferred
	// keystroke too. The ranking and the search are read when the pick happens, as the list the user tapped showed them.
	const pickContextRef = useLatestRef({ order, isSearched: query !== "" });
	const handlePick = useCallback(
		(candidate: GapsAddCandidate, index: number): void => {
			const { order: pickedFrom, isSearched } = pickContextRef.current;
			const properties = {
				department_id: chosenDepartment.department_id,
				nurse_id: candidate.nurse.id,
				rank: index + 1,
				order: pickedFrom,
				searched: isSearched,
			};
			track("gaps_add_staff_candidate_selected", properties);
			setSelectedId(candidate.nurse.id);
		},
		[chosenDepartment.department_id, pickContextRef, track],
	);

	return (
		<>
			<SheetStep
				stepKey={`${SHEET}-${group.key}`}
				dataTestId={SHEET}
				leading={<GapsJobBadge group={group} dataTestId={`${SHEET}-job-badge`} />}
				title={t("gaps.addSheet.title")}
				subtitle={subtitle}
				toolbar={
					<SystemSearchField
						variant="sheet"
						fullWidth
						value={search}
						onChange={setSearch}
						placeholder={t("gaps.addSheet.search")}
						name={`${SHEET}-search`}
						dataTestId={`${SHEET}-search`}
					/>
				}
				footer={
					<>
						<SheetStepButton
							tone="dismiss"
							// DEFAULT: an opening with nowhere to go back to (scoped to a hole, or placed on the day) closes instead.
							onClick={onBack ?? onClose}
							disabled={isBusy}
							dataTestId={`${SHEET}-cancel`}
						>
							{onBack === undefined ? t("cancel") : t("back")}
						</SheetStepButton>
						<SheetStepButton
							tone="confirm"
							onClick={() => {
								if (selected !== undefined) void add(selected.nurse);
							}}
							disabled={selected === undefined || isLinkedRequestGone || isWriteBlocked}
							loading={isBusy}
							dataTestId={`${SHEET}-confirm`}
						>
							{selected === undefined ? t("gaps.addSheet.add") : t("gaps.addSheet.addName", { name: selected.nurse.firstName })}
						</SheetStepButton>
					</>
				}
			>
				{/* The day is the shift's, so it is stated, not offered (APP-886): picking another day is the picker's job. */}
				<Box sx={{ px: 2, pt: 2 }}>
					<GapsDateField date={date} homeWeekShortfalls={undefined} onPick={undefined} testIdPrefix={SHEET} />
				</Box>
				<GapsAddStepHints
					linkedRequest={linkedRequest}
					isLinkedRequestGone={isLinkedRequestGone}
					isRankingFailed={isRankingFailed}
					isRosterError={isRosterError}
					blockedJobLabel={isWriteBlocked ? group.jobLabel : null}
					testIdPrefix={SHEET}
				/>
				{!isRosterError && (
					<GapsAddCandidateList
						listLabel={listLabel}
						// The list's skeleton covers the roster's load as well as the ranking's.
						isRanking={isRanking || isRosterLoading}
						visible={visible}
						// The list is in the backend's cost order and nothing has re-sorted it: a search filters it by name.
						isRankedList={order === GapsAddOrder.Cost && query === ""}
						selectedId={selectedId}
						dayGroups={dayGroups}
						isBusy={isBusy}
						onPick={handlePick}
						hiddenCount={hiddenCount}
						onShowMore={() => {
							track("gaps_add_staff_show_more", { department_id: chosenDepartment.department_id, hidden: hiddenCount });
							setIsShowingAll(true);
						}}
						poolSize={poolSize}
						matchCount={matches.length}
						query={query}
						testIdPrefix={SHEET}
					/>
				)}
				{linkedRequest === null && onPostInstead !== undefined && (
					<Box sx={{ mt: 1, borderTop: `1px solid ${NEUTRAL[150]}` }}>
						<SheetActionRow
							icon={<AddCircleOutlineIcon />}
							label={t("gaps.addSheet.postInstead")}
							description={t("gaps.addSheet.postInsteadHint")}
							showChevron
							disabled={isBusy || isWriteBlocked}
							onClick={() => onPostInstead(group.key)}
							dataTestId={`${SHEET}-post-instead`}
						/>
					</Box>
				)}
			</SheetStep>
			<GapsAddWarningDialog warnings={warnings} onCancel={cancelWarnings} />
		</>
	);
};
